// React and Router
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Firebase
import { auth, updateUserEmail } from "../../firebase.js"
import { onAuthStateChanged } from "firebase/auth";
import { useContextMenu } from "../shared/ContextMenu";

// Components
import Header from "../shared/Header"
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { useTheme } from "../../contexts/ThemeContext";
import { THEMES, THEME_LABELS } from "../../utils/theme";

// Stripe

// Styles
import "../../css/Account.css"
import "../../css/Create.css"

function Account() {
        const { updateEnhancedRightClick: updateGlobalEnhancedRightClick, updateNotificationPreferences: updateGlobalNotificationPreferences, updateSnapshotFrequency: updateGlobalSnapshotFrequency } = useQuickLinks();

        const { uid } = useParams()
        const [accountEmail, setAccountEmail] = useState("")
        const [accountName, setAccountName] = useState("")
        const [accountCreatedDate, setAccountCreatedDate] = useState("")
        const [accountLastLoginDate, setAccountLastLoginDate] = useState("")
        const [accountVipStatus, setAccountVipStatus] = useState(false)
        const [accountSubscriberStatus, setAccountSubscriberStatus] = useState(false)
        const [accountThemeAccess, setAccountThemeAccess] = useState(false)
        const { personalTheme: accountTheme, setTheme } = useTheme();
        const [themeError, setThemeError] = useState("");
        const navigate = useNavigate()
        const url = window.location.pathname.split('/').pop();

        const [customLink, setCustomLink] = useState("")
        const [customLinkTitle, setCustomLinkTitle] = useState("")
        const [discordLink, setDiscordLink] = useState("")
        const [linkedInLink, setLinkedInLink] = useState("")
        const [youtubeLink, setYoutubeLink] = useState("")
        const [githubLink, setGithubLink] = useState("")
        const [blueskyLink, setBlueskyLink] = useState("")
        const [twitterLink, setTwitterLink] = useState("")

        const [originalLinks, setOriginalLinks] = useState({
            customLink: "",
            customLinkTitle: "",
            discordLink: "",
            linkedInLink: "",
            youtubeLink: "",
            githubLink: "",
            twitterLink: "",
            blueskyLink: ""
        });

        // Quick Navigation Links State
        const [quickLinks, setQuickLinks] = useState([]);
        const [newQuickLink, setNewQuickLink] = useState({ name: '', url: '' });
        const [editingLinkId, setEditingLinkId] = useState(null);
        const [editingLink, setEditingLink] = useState({ name: '', url: '' });
        const [quickLinksError, setQuickLinksError] = useState('');

        const [isAuthChecked, setIsAuthChecked] = useState(false);
        const [isLoggedIn, setIsLoggedIn] = useState(true);
        const [enhancedRightClickEnabled, setEnhancedRightClickEnabled] = useState(true);
        const [activeSection, setActiveSection] = useState("profile");

        // Notification preferences state
        const [notificationPreferences, setNotificationPreferences] = useState({
                emailNotifications: true,
                commentNotifications: true,
                documentSharedNotifications: true,
                documentUpdatedNotifications: true,
                mentionNotifications: true,
                workspaceNotifications: true,
                reminderNotifications: true,
                exportNotifications: true
        });
        const [originalNotificationPreferences, setOriginalNotificationPreferences] = useState({});

        // Snapshot preferences state
        const [snapshotFrequencyMinutes, setSnapshotFrequencyMinutes] = useState(5);
        const [originalSnapshotFrequency, setOriginalSnapshotFrequency] = useState(5);

        // Blocked emails state (BBD-73)
        const [blockedEmails, setBlockedEmails] = useState([]);
        const [blockedError, setBlockedError] = useState('');
        const [newBlockedEmail, setNewBlockedEmail] = useState('');
        const [newBlockedNote, setNewBlockedNote] = useState('');
        const [blockedLoading, setBlockedLoading] = useState(false);
        const [editingBlockId, setEditingBlockId] = useState(null);
        const [editingBlockNote, setEditingBlockNote] = useState('');

        // API Keys state
        const [apiKeys, setApiKeys] = useState([]);
        const [apiKeysError, setApiKeysError] = useState('');
        const [newKeyName, setNewKeyName] = useState('');
        const [newKeyScopes, setNewKeyScopes] = useState({ read: true, write: false, delete: false });
        const [newKeyRPH, setNewKeyRPH] = useState(100);
        const [generatedKey, setGeneratedKey] = useState(null);
        const [apiKeysLoading, setApiKeysLoading] = useState(false);

        useEffect(() => {
                const query = new URLSearchParams(window.location.search);
                const sessionId = query.get("session_id");
                if (sessionId) {
                        verifySession(sessionId);
                }
        }, [url]);

        const handleSelectedTheme = async (e) => {
                setThemeError("");
                const result = await setTheme(e.target.value);
                if (!result.ok) {
                        setThemeError(result.error || "Could not save your theme.");
                }
        }

                useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged(async (user) => {
                        if (user && user.uid === uid) {
                                setIsAuthChecked(true);
                                setIsLoggedIn(true);
                                try {
                                        await Promise.all([
                                                fetchAccountDetails(),
                                                fetchQuickLinks(),
                                                fetchNotificationPreferences(),
                                                fetchApiKeys(),
                                                fetchBlockedEmails()
                                        ]);
                                } catch (error) {
                                        console.error('Error fetching user data:', error);
                                }
                        } else if (user && user.uid !== uid) {
                                // Different user, redirect to login
                                setIsLoggedIn(false);
                                setIsAuthChecked(true);
                        } else {
                                // No user
                                setIsLoggedIn(false);
                                setIsAuthChecked(true);
                        }
                });

                return () => unsubscribe();
        }, [uid]);

        // Listen for Firebase auth state changes and fetch quick links when ready
        useEffect(() => {
                let unsubscribe;
                if (uid) {
                        unsubscribe = onAuthStateChanged(auth, (user) => {
                                if (user) {
                                        fetchQuickLinks();
                                        fetchAccountDetails();
                                        fetchNotificationPreferences();
                                        fetchApiKeys();
                                        fetchBlockedEmails();
                                } else {
                                        setQuickLinks([]);
                                        setApiKeys([]);
                                        setBlockedEmails([]);
                                        setNotificationPreferences({
                                                emailNotifications: true,
                                                commentNotifications: true,
                                                documentSharedNotifications: true,
                                                documentUpdatedNotifications: true,
                                                mentionNotifications: true,
                                                workspaceNotifications: true,
                                                reminderNotifications: true,
                                                exportNotifications: true
                                        });
                                }
                        });
                }
                return () => {
                        if (unsubscribe) unsubscribe();
                };
        }, [uid]);

const loggedInOptions = [
    {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/")
    },
    {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => navigate("/documentation"),
    },
    {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => navigate("/walkthroughs"),
    },
    {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => navigate("/my-notebook"),
    },
    {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => navigate("/workspaces"),
    }
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = loggedInOptions;
  const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

        const formatTimestamp = (timestamp) => {
                const date = new Date(timestamp);
                return date.toLocaleString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                });
        };

        const fetchAccountDetails = async () => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/`, {
                                headers: {
                                        'Authorization': `${token}`
                                }
                        });
                        const data = await response.json();

                        if (response.ok) {
                                setAccountEmail(data.email);
                                setAccountName(data.name);
                                setAccountCreatedDate(formatTimestamp(data.created_at));
                                setAccountLastLoginDate(formatTimestamp(data.last_login));
                                setAccountVipStatus(data.is_vip);
                                setAccountSubscriberStatus(data.is_subscriber);
                                // Themes come with any paid storage plan or VIP (BBD-99).
                                setAccountThemeAccess(typeof data.has_theme_access === "boolean"
                                        ? data.has_theme_access
                                        : Boolean(data.is_subscriber || data.is_vip));
                                setEnhancedRightClickEnabled(data.enhanced_right_click_enabled);
                                setYoutubeLink(data.youtube_channel);
                                setCustomLink(data.custom_domain);
                                setCustomLinkTitle(data.custom_domain_title);
                                setDiscordLink(data.discord_server);
                                setBlueskyLink(data.bluesky_link);
                                setLinkedInLink(data.linkedin_link);
                                setGithubLink(data.github_link);
                                setTwitterLink(data.twitter_link);
                                setSnapshotFrequencyMinutes(data.snapshot_frequency_minutes || 5);
                                setOriginalSnapshotFrequency(data.snapshot_frequency_minutes || 5);
                                // Also store original values for comparison
                                setOriginalLinks({
                                  customLink: data.custom_domain,
                                  customLinkTitle: data.custom_domain_title,
                                  discordLink: data.discord_server,
                                  linkedInLink: data.linkedin_link,
                                  youtubeLink: data.youtube_channel,
                                  githubLink: data.github_link,
                                  twitterLink: data.twitter_link,
                                  blueskyLink: data.bluesky_link
                                });
                        }

                } catch (error) {
                        console.error("Error in fetching account details: ", error);
                }
        }

        const saveAccountName = async () => {
                try {
                        const data = {
                                name: accountName
                        }

                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }

                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/update/${uid}/`, {
                                method: "PATCH",
                                headers: { 
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                },
                                body: JSON.stringify(data)
                        })

                        if (response.ok) {
                                alert("Account name changed successfully!")
                        } else {
                                alert("Failed to update account name.")
                        }
                } catch (error) {
                        alert("Failed to change account name: ", error)
                }
        }

        const saveAccountEmail = async () => {
                try {
                        const data = {
                                email: accountEmail,
                                accountUid: uid
                        }
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }
                        await updateUserEmail(accountEmail);
                        alert("A verification email has been sent. Please confirm the change before logging in again.");
                } catch (error) {
                        alert("Failed to change account email.")
                }
        }

        const saveCustomLink = async () => {
                try {
                        const data = {
                                customLink: customLink,
                                customLinkTitle: customLinkTitle,
                                twitterLink: twitterLink,
                                discordLink: discordLink,
                                linkedInLink: linkedInLink,
                                githubLink: githubLink,
                                youtubeLink: youtubeLink,
                                blueskyLink: blueskyLink
                        }
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/update/${uid}/link/`, {
                                method: "PATCH",
                                headers: { 
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                },
                                body: JSON.stringify(data)
                        })
                        if (response.ok) {
                                alert("Custom link saved successfully!")
                        } else {
                                alert("Failed to save custom link.")
                        }
                } catch (error) {
                        alert("Failed to save custom link: ", error)
                }
        }

        const updateAccountSubscriberStatus = async (isSubscriber) => {
                try {

                        const data = {
                                isSubscriber: isSubscriber
                        }

                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }

                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/subscriber-status/`, {
                                method: "PATCH",
                                headers: { 
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                },
                                body: JSON.stringify(data)
                        })

                        if (response.ok) {
                                navigate(`/account/${uid}`)
                                window.location.reload()
                                alert("Subscriber status changed successfully!")
                        } else {
                                alert("Failed to update subscriber status. Please contact us immediately if you've received any unexpected charges from us.")
                        }
                } catch (error) {
                        alert("Failed to update subscriber status. Please contact us immediately if you've received any unexpected charges from us. Error: ", error)
                }
        }

        const updateEnhancedRightClick = async (enabled) => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;

                        const token = await user.getIdToken();
                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/users/update-right-click-status/", {
                                method: "PATCH",
                                headers: {
                                        "Content-Type": "application/json",
                                        "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
                                },
                                body: JSON.stringify({
                                        enhancedRightClickEnabled: enabled
                                })
                        });

                        if (response.ok) {
                                setEnhancedRightClickEnabled(enabled);
                                // Update the global state as well
                                updateGlobalEnhancedRightClick(enabled);
                        } else {
                                console.error("Failed to update enhanced right-click status");
                        }
                } catch (error) {
                        console.error("Error updating enhanced right-click status:", error);
                }
        };

        const handleEnhancedRightClickToggle = (e) => {
                const enabled = e.target.checked;
                updateEnhancedRightClick(enabled);
        };

        const verifySession = async (sessionId) => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) {
                                console.error("User is not authenticated.");
                                return;
                        }
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/verify-checkout-session/${sessionId}/${uid}/`, {
                                headers: {
                                        "Authorization": `${token}`,
                                },
                        });
                        const data = await response.json();
                        if (data.payment_status === "paid") {
                                alert("Thank you for subscribing!")
                        } else {
                                alert("Payment not completed.");
                        }
                } catch (error) {
                        console.error("Error verifying session:", error);
                }
        };

        async function handleManageSubscriptionClick() {
                const user = auth.currentUser;
                if (!user) return;

                try {
                        const token = await user.getIdToken();
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/storage/billing-portal/`, {
                                method: "POST",
                                headers: {
                                        "Content-Type": "application/json",
                                        "Authorization": `${token}`,
                                        "auth-token": `Bearer ${token}`,
                                        "requesting-user-uid": uid,
                                },
                                body: JSON.stringify({
                                        return_url: `${window.location.origin}/account/${uid}`,
                                }),
                        });

                        if (response.ok) {
                                const data = await response.json();
                                window.open(data.portal_url, "_blank");
                        } else {
                                // Fall back to the generic (non-pre-authenticated) portal login
                                // link, e.g. for users with no Stripe customer ID yet.
                                window.open("https://billing.stripe.com/p/login/aEU5mr1XU6fjcWAcMM", "_blank");
                        }
                } catch (error) {
                        console.error("Error opening billing portal:", error);
                        window.open("https://billing.stripe.com/p/login/aEU5mr1XU6fjcWAcMM", "_blank");
                }
        }

        const handleBackToHome = () => { navigate('/') }
        const handleNotebookSettingsClick = () => { navigate('/my-notebook/settings') }

        const fetchQuickLinks = async () => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/quick-links/`, {
                                headers: {
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                }
                        });
                        if (res.ok) {
                                const data = await res.json();
                                setQuickLinks(data);
                        }
                } catch (e) {
                        setQuickLinksError('Failed to load quick links.');
                }
        };

        const fetchNotificationPreferences = async () => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;
                        
                        const token = await user.getIdToken();
                        
                        // Fetch from user detail endpoint to get actual preference values
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/`, {
                                method: 'GET',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                }
                        });
                        
                        if (response.ok) {
                                const userData = await response.json();
                                // Map backend field names to frontend keys
                                const preferences = {
                                        emailNotifications: userData.email_notifications_enabled ?? true,
                                        commentNotifications: userData.comment_notifications_enabled ?? true,
                                        documentSharedNotifications: userData.document_notifications_enabled ?? true,
                                        documentUpdatedNotifications: userData.document_notifications_enabled ?? true,
                                        mentionNotifications: userData.mention_notifications_enabled ?? true,
                                        workspaceNotifications: userData.workspace_management_notifications_enabled ?? true,
                                        reminderNotifications: userData.reminder_notifications_enabled ?? true,
                                        exportNotifications: userData.export_notifications_enabled ?? true
                                };
                                setNotificationPreferences(preferences);
                                setOriginalNotificationPreferences(preferences);
                        }
                } catch (error) {
                        console.error('Error fetching notification preferences:', error);
                }
        };

        const BLOCKED_EMAILS_URL = 'https://bluebirddocumentationadmin.pythonanywhere.com';

        const fetchBlockedEmails = async () => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`${BLOCKED_EMAILS_URL}/users/${uid}/blocked-emails/`, {
                                headers: { 'Authorization': token }
                        });
                        if (res.ok) {
                                setBlockedEmails(await res.json());
                        }
                } catch (e) {
                        console.error('Failed to load blocked emails:', e);
                }
        };

        const addBlockedEmail = async () => {
                const email = newBlockedEmail.trim().toLowerCase();
                if (!email) {
                        setBlockedError('Enter an email address to block.');
                        return;
                }
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        setBlockedError('That does not look like a valid email address.');
                        return;
                }
                setBlockedLoading(true);
                setBlockedError('');
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`${BLOCKED_EMAILS_URL}/users/${uid}/blocked-emails/create/`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                                body: JSON.stringify({ email, note: newBlockedNote.trim() })
                        });
                        const data = await res.json();
                        if (res.ok) {
                                setNewBlockedEmail('');
                                setNewBlockedNote('');
                                fetchBlockedEmails();
                        } else {
                                setBlockedError(data.error || 'Failed to block that address.');
                        }
                } catch (e) {
                        setBlockedError('Failed to block that address.');
                } finally {
                        setBlockedLoading(false);
                }
        };

        const saveBlockedNote = async (blockId) => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`${BLOCKED_EMAILS_URL}/users/blocked-emails/${blockId}/update/`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                                body: JSON.stringify({ note: editingBlockNote })
                        });
                        if (res.ok) {
                                setEditingBlockId(null);
                                setEditingBlockNote('');
                                fetchBlockedEmails();
                        } else {
                                setBlockedError('Failed to save the note.');
                        }
                } catch (e) {
                        setBlockedError('Failed to save the note.');
                }
        };

        const removeBlockedEmail = async (blockId, email) => {
                if (!window.confirm(`Unblock ${email}? Anything they could see before the block \u2014 shared documents, workspaces, boards \u2014 becomes visible to them again.`)) return;
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`${BLOCKED_EMAILS_URL}/users/blocked-emails/${blockId}/delete/`, {
                                method: 'DELETE',
                                headers: { 'Authorization': token }
                        });
                        if (res.ok) {
                                fetchBlockedEmails();
                        } else {
                                setBlockedError('Failed to unblock that address.');
                        }
                } catch (e) {
                        setBlockedError('Failed to unblock that address.');
                }
        };

        const fetchApiKeys = async () => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/users/api-keys/', {
                                headers: { 'Authorization': token }
                        });
                        if (res.ok) {
                                const data = await res.json();
                                setApiKeys(data.api_keys);
                        }
                } catch (e) {
                        console.error('Failed to load API keys:', e);
                }
        };

        const createApiKey = async () => {
                if (!newKeyName.trim()) {
                        setApiKeysError('Key name is required.');
                        return;
                }
                const scopes = Object.entries(newKeyScopes).filter(([, v]) => v).map(([k]) => k);
                if (scopes.length === 0) {
                        setApiKeysError('Select at least one permission scope.');
                        return;
                }
                setApiKeysLoading(true);
                setApiKeysError('');
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/users/api-keys/create/', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': token },
                                body: JSON.stringify({ name: newKeyName.trim(), scopes, requests_per_hour: newKeyRPH })
                        });
                        const data = await res.json();
                        if (res.ok) {
                                setGeneratedKey(data.key);
                                setNewKeyName('');
                                setNewKeyScopes({ read: true, write: false, delete: false });
                                setNewKeyRPH(100);
                                fetchApiKeys();
                        } else {
                                setApiKeysError(data.error || 'Failed to create API key.');
                        }
                } catch (e) {
                        setApiKeysError('Failed to create API key.');
                } finally {
                        setApiKeysLoading(false);
                }
        };

        const revokeApiKey = async (keyId, keyName) => {
                if (!window.confirm(`Revoke the key "${keyName}"? This cannot be undone.`)) return;
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/api-keys/${keyId}/revoke/`, {
                                method: 'DELETE',
                                headers: { 'Authorization': token }
                        });
                        if (res.ok) {
                                fetchApiKeys();
                        } else {
                                setApiKeysError('Failed to revoke key.');
                        }
                } catch (e) {
                        setApiKeysError('Failed to revoke key.');
                }
        };

        const saveNotificationPreferences = async () => {
                try {
                        const user = auth.currentUser;
                        if (!user) return;
                        
                        const token = await user.getIdToken();
                        
                        // Map frontend keys to backend field names
                        const backendPayload = {
                                email_notifications_enabled: notificationPreferences.emailNotifications,
                                comment_notifications_enabled: notificationPreferences.commentNotifications,
                                document_notifications_enabled: notificationPreferences.documentSharedNotifications, // Using documentSharedNotifications as the main toggle
                                subscription_notifications_enabled: notificationPreferences.subscriptionNotifications ?? true,
                                workspace_management_notifications_enabled: notificationPreferences.workspaceNotifications,
                                mention_notifications_enabled: notificationPreferences.mentionNotifications,
                                reminder_notifications_enabled: notificationPreferences.reminderNotifications,
                                export_notifications_enabled: notificationPreferences.exportNotifications ?? true
                        };
                        
                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/notification-preferences/update/`, {
                                method: 'PATCH',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                },
                                body: JSON.stringify(backendPayload)
                        });
                        
                        if (response.ok) {
                                setOriginalNotificationPreferences({ ...notificationPreferences });
                                // Update global state so Header component can filter notifications
                                // Map to the format expected by global state
                                const globalPrefs = {
                                        emailNotifications: notificationPreferences.emailNotifications,
                                        commentNotifications: notificationPreferences.commentNotifications,
                                        documentNotifications: notificationPreferences.documentSharedNotifications,
                                        subscriptionNotifications: notificationPreferences.subscriptionNotifications ?? true,
                                        workspaceNotifications: notificationPreferences.workspaceNotifications,
                                        mentionNotifications: notificationPreferences.mentionNotifications,
                                        reminderNotifications: notificationPreferences.reminderNotifications,
                                        exportNotifications: notificationPreferences.exportNotifications ?? true
                                };
                                updateGlobalNotificationPreferences(globalPrefs);
                        } else {
                                console.error('Failed to save notification preferences');
                        }
                } catch (error) {
                        console.error('Error saving notification preferences:', error);
                }
        };

        const handleNotificationPreferenceChange = (key, value) => {
                setNotificationPreferences(prev => ({
                        ...prev,
                        [key]: value
                }));
        };

        const saveSnapshotPreferences = async () => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;

                        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/snapshot-preferences/update/`, {
                                method: 'PUT',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`,
                                        'Requesting-User-Uid': user.uid
                                },
                                body: JSON.stringify({
                                        snapshot_frequency_minutes: snapshotFrequencyMinutes
                                })
                        });

                        if (response.ok) {
                                setOriginalSnapshotFrequency(snapshotFrequencyMinutes);
                                updateGlobalSnapshotFrequency(snapshotFrequencyMinutes);
                        } else {
                                console.error('Failed to save snapshot preferences');
                        }
                } catch (error) {
                        console.error('Error saving snapshot preferences:', error);
                }
        };

        const handleSnapshotFrequencyChange = (value) => {
                const frequency = parseInt(value);
                if (frequency >= 5 && frequency <= 30) {
                        setSnapshotFrequencyMinutes(frequency);
                }
        };

        const handleAddQuickLink = async () => {
                if (quickLinks.length >= 5) {
                        setQuickLinksError('You can only have up to 3 quick links.');
                        return;
                }
                if (!newQuickLink.name.trim() || !newQuickLink.url.trim()) {
                        setQuickLinksError('Both name and URL are required.');
                        return;
                }
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/quick-links/create/`, {
                                method: 'POST',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                },
                                body: JSON.stringify(newQuickLink)
                        });
                        if (res.ok) {
                                setNewQuickLink({ name: '', url: '' });
                                setQuickLinksError('');
                                fetchQuickLinks();
                        } else {
                                setQuickLinksError('Failed to add quick link.');
                        }
                } catch (e) {
                        setQuickLinksError('Failed to add quick link.');
                }
        };

        const handleEditQuickLink = (link) => {
                setEditingLinkId(link.id);
                setEditingLink({ name: link.name, url: link.url });
                setQuickLinksError('');
        };

        const handleSaveEditQuickLink = async (linkId) => {
                if (!editingLink.name.trim() || !editingLink.url.trim()) {
                        setQuickLinksError('Both name and URL are required.');
                        return;
                }
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/quick-links/${linkId}/update/`, {
                                method: 'POST',
                                headers: {
                                        'Content-Type': 'application/json',
                                        'auth-token': `Bearer ${token}`,
                                        'Authorization': `${token}`
                                },
                                body: JSON.stringify(editingLink)
                        });
                        if (res.ok) {
                                setEditingLinkId(null);
                                setEditingLink({ name: '', url: '' });
                                setQuickLinksError('');
                                fetchQuickLinks();
                        } else {
                                setQuickLinksError('Failed to update quick link.');
                        }
                } catch (e) {
                        setQuickLinksError('Failed to update quick link.');
                }
        };

        const handleDeleteQuickLink = async (linkId) => {
                try {
                        const user = auth.currentUser;
                        const token = user ? await user.getIdToken() : null;
                        if (!token) return;
                        const res = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/users/quick-links/${linkId}/delete/`, {
                                method: 'DELETE',
                                headers: { 
                                    'auth-token': `Bearer ${token}`,
                                    'Authorization': `${token}`
                                }
                        });
                        if (res.ok) {
                                setQuickLinksError('');
                                fetchQuickLinks();
                        } else {
                                setQuickLinksError('Failed to delete quick link.');
                        }
                } catch (e) {
                        setQuickLinksError('Failed to delete quick link.');
                }
        };

        // Redirect to login if not authenticated
        useEffect(() => {
                const unsubscribe = onAuthStateChanged(auth, (user) => {
                        setIsAuthChecked(true);
                        if (!user) {
                                setIsLoggedIn(false);
                        } else {
                                setIsLoggedIn(true);
                        }
                });
                return () => unsubscribe();
        }, []);

        if (isAuthChecked && !isLoggedIn) {
                return (
                        <div className="Account__main-content">
                                {navigate('/login')}
                        </div>
                );
        }

        const renderProfile = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Profile</h2>
                                <p className="Account__section-desc">Manage your account name, email, and appearance theme.</p>
                        </div>

                        <p className="small" style={{ marginBottom: "8px" }}>Account Created on: {accountCreatedDate}</p>
                        <p className="small" style={{ marginBottom: "24px" }}>Last Logged in: {accountLastLoginDate}</p>

                        <div className="Account__field-row">
                                <label className="Account__field-label">Your Displayed Name</label>
                                <input
                                        id="accountNameInput"
                                        type="text"
                                        value={accountName}
                                        onChange={(e) => setAccountName(e.target.value)}
                                        onBlur={() => {
                                                if (accountName.trim()) {
                                                        saveAccountName();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__field-row">
                                <label className="Account__field-label">Email Address</label>
                                <input
                                        id="accountEmail"
                                        type="email"
                                        value={accountEmail}
                                        onChange={(e) => setAccountEmail(e.target.value)}
                                        onBlur={() => {
                                                const user = auth.currentUser;
                                                if (user && user.email && accountEmail.trim() && accountEmail.trim() !== user.email) {
                                                        saveAccountEmail();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__field-row">
                                <label className="Account__field-label">Theme</label>
                                {accountThemeAccess ? (
                                        <>
                                                <select
                                                        className="Account__select-field"
                                                        aria-label="Select theme"
                                                        name="account_theme"
                                                        value={accountTheme}
                                                        onChange={handleSelectedTheme}
                                                        required
                                                >
                                                        {THEMES.map((id) => (
                                                                <option key={id} value={id}>{THEME_LABELS[id]}</option>
                                                        ))}
                                                </select>
                                                {themeError && <p className="Account__field-error">{themeError}</p>}
                                                {accountSubscriberStatus && (
                                                <div style={{ marginTop: "16px" }}>
                                                        <a
                                                                href=""
                                                                onClick={handleManageSubscriptionClick}
                                                        >
                                                                Manage your subscription.
                                                        </a>
                                                </div>
                                                )}
                                        </>
                                ) : (
                                        <>
                                                <p style={{ marginBottom: "12px" }}>Themes are included with <strong>any paid storage plan</strong>. Upgrade to unlock Dark and Purple across the whole app.</p>
                                                <button onClick={() => navigate(`/account/${uid}/plans`)} className="Account__subscriber-button">View storage plans</button>
                                        </>
                                )}
                        </div>
                </>
        );

        const renderSocial = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Social Links</h2>
                                <p className="Account__section-desc">Expand your social network by adding links below. You can choose which to show in future documentation you create.</p>
                        </div>

                        <div className="Account__field-row">
                                <label className="Account__field-label">Custom Link</label>
                                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                        <input
                                                type="text"
                                                placeholder="Custom Link Title"
                                                value={customLinkTitle}
                                                onChange={(e) => setCustomLinkTitle(e.target.value)}
                                                className="Account__input-custom-title"
                                                onBlur={() => {
                                                        if (customLinkTitle !== originalLinks.customLinkTitle) {
                                                                if (customLink.trim().length > 0) {
                                                                        saveCustomLink();
                                                                }
                                                        }
                                                }}
                                        />
                                        <input
                                                id="customLinkInput"
                                                type="text"
                                                placeholder="Custom Link Domain"
                                                value={customLink}
                                                onChange={(e) => setCustomLink(e.target.value)}
                                                onBlur={() => {
                                                        if (customLink !== originalLinks.customLink) {
                                                                if (customLinkTitle.trim().length === 0 && customLink.trim().length > 0) {
                                                                        alert("Please enter a title for your custom domain.");
                                                                } else if (customLink.trim()) {
                                                                        saveCustomLink();
                                                                }
                                                        }
                                                }}
                                                className="Account__edit-input"
                                        />
                                </div>
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-discord Account__icons"></i>
                                <input
                                        type="text"
                                        value={discordLink}
                                        placeholder="Discord Server Link"
                                        onChange={(e) => setDiscordLink(e.target.value)}
                                        onBlur={() => {
                                                if (discordLink !== originalLinks.discordLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-square-x-twitter Account__icons"></i>
                                <input
                                        type="text"
                                        value={twitterLink}
                                        placeholder="X Profile Link"
                                        onChange={(e) => setTwitterLink(e.target.value)}
                                        onBlur={() => {
                                                if (twitterLink !== originalLinks.twitterLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-bluesky Account__icons"></i>
                                <input
                                        type="text"
                                        value={blueskyLink}
                                        placeholder="Bluesky Profile Link"
                                        onChange={(e) => setBlueskyLink(e.target.value)}
                                        onBlur={() => {
                                                if (blueskyLink !== originalLinks.blueskyLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-linkedin Account__icons"></i>
                                <input
                                        type="text"
                                        value={linkedInLink}
                                        placeholder="LinkedIn Profile Link"
                                        onChange={(e) => setLinkedInLink(e.target.value)}
                                        onBlur={() => {
                                                if (linkedInLink !== originalLinks.linkedInLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-youtube Account__icons"></i>
                                <input
                                        type="text"
                                        value={youtubeLink}
                                        placeholder="YouTube Channel Link"
                                        onChange={(e) => setYoutubeLink(e.target.value)}
                                        onBlur={() => {
                                                if (youtubeLink !== originalLinks.youtubeLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>

                        <div className="Account__social-field-row Account__link-divs">
                                <i className="fa-brands fa-square-github Account__icons"></i>
                                <input
                                        type="text"
                                        value={githubLink}
                                        placeholder="GitHub Profile Link"
                                        onChange={(e) => setGithubLink(e.target.value)}
                                        onBlur={() => {
                                                if (githubLink !== originalLinks.githubLink) {
                                                        saveCustomLink();
                                                }
                                        }}
                                        className="Account__edit-input"
                                />
                        </div>
                </>
        );

        const renderNotifications = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Notifications</h2>
                                <p className="Account__section-desc">Control which types of notifications you receive.</p>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">Email Notifications</span>
                                        <p className="Account__toggle-description">Receive email notifications in addition to notifications within the app</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={notificationPreferences.emailNotifications}
                                                onChange={(e) => handleNotificationPreferenceChange('emailNotifications', e.target.checked)}
                                                onBlur={saveNotificationPreferences}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">Comment Notifications</span>
                                        <p className="Account__toggle-description">Get notified when someone comments on your documents</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={notificationPreferences.commentNotifications}
                                                onChange={(e) => handleNotificationPreferenceChange('commentNotifications', e.target.checked)}
                                                onBlur={saveNotificationPreferences}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">Document Shared Notifications</span>
                                        <p className="Account__toggle-description">Get notified when documents are shared with you</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={notificationPreferences.documentSharedNotifications}
                                                onChange={(e) => handleNotificationPreferenceChange('documentSharedNotifications', e.target.checked)}
                                                onBlur={saveNotificationPreferences}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">Workspace Notifications</span>
                                        <p className="Account__toggle-description">Get notified about workspace updates and invitations</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={notificationPreferences.workspaceNotifications}
                                                onChange={(e) => handleNotificationPreferenceChange('workspaceNotifications', e.target.checked)}
                                                onBlur={saveNotificationPreferences}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">PDF Export Notifications</span>
                                        <p className="Account__toggle-description">Get notified when a large PDF export has finished and is ready to download</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={notificationPreferences.exportNotifications ?? true}
                                                onChange={(e) => handleNotificationPreferenceChange('exportNotifications', e.target.checked)}
                                                onBlur={saveNotificationPreferences}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>
                </>
        );

        const renderPreferences = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Preferences</h2>
                                <p className="Account__section-desc">Configure app behaviour and document auto-save settings.</p>
                        </div>

                        <div className="Account__toggle-row">
                                <div className="Account__toggle-label">
                                        <span className="Account__toggle-label-text">Enhanced Right-Click Menu</span>
                                        <p className="Account__toggle-description">Enable enhanced right-click context menu with quick navigation links</p>
                                </div>
                                <label className="Create__switch">
                                        <input
                                                type="checkbox"
                                                checked={enhancedRightClickEnabled}
                                                onChange={handleEnhancedRightClickToggle}
                                        />
                                        <span className="Create__slider round"></span>
                                </label>
                        </div>

                        <div className="Account__field-row" style={{ marginTop: "28px" }}>
                                <label className="Account__field-label">Auto-save Frequency (Snapshots)</label>
                                <select
                                        className="Account__select-field"
                                        value={snapshotFrequencyMinutes}
                                        onChange={(e) => handleSnapshotFrequencyChange(e.target.value)}
                                        onBlur={saveSnapshotPreferences}
                                >
                                        <option value={5}>Every 5 minutes</option>
                                        <option value={10}>Every 10 minutes</option>
                                        <option value={15}>Every 15 minutes</option>
                                        <option value={20}>Every 20 minutes</option>
                                        <option value={30}>Every 30 minutes</option>
                                </select>
                        </div>
                </>
        );

        const renderQuickLinks = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Quick Links</h2>
                                <p className="Account__section-desc">Add up to 5 quick navigation links accessible from the right-click menu.</p>
                        </div>

                        {quickLinksError && <div style={{ color: 'red', marginBottom: 8 }}>{quickLinksError}</div>}

                        {quickLinks.map((link) => (
                                <div className="Account__link-divs Account__quick-link-item" key={link.id}>
                                        <div className="Account__quick-link-header">
                                                <i className="fa-solid fa-link Account__icons"></i>
                                                <span className="Account__quick-link-name">{link.name}:</span>
                                        </div>
                                        {editingLinkId === link.id ? (
                                                <>
                                                        <input
                                                                type="text"
                                                                value={editingLink.name}
                                                                placeholder="Link Name"
                                                                onChange={e => setEditingLink({ ...editingLink, name: e.target.value })}
                                                                className="Account__input-custom-title"
                                                        />
                                                        <input
                                                                type="text"
                                                                value={editingLink.url}
                                                                placeholder="Link URL"
                                                                onChange={e => setEditingLink({ ...editingLink, url: e.target.value })}
                                                                className="Account__edit-input"
                                                        />
                                                        <div className="Account__quick-link-buttons">
                                                                <button className="Account__navigation-buttons" onClick={() => handleSaveEditQuickLink(link.id)}>Save</button>
                                                                <button className="Account__navigation-buttons Account__quick-link-delete" onClick={() => setEditingLinkId(null)}>Cancel</button>
                                                        </div>
                                                </>
                                        ) : (
                                                <>
                                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="Account__quick-link-url">{link.url}</a>
                                                        <div className="Account__quick-link-buttons">
                                                                <button className="Account__navigation-buttons" onClick={() => handleEditQuickLink(link)}>Edit</button>
                                                                <button className="Account__navigation-buttons Account__quick-link-delete" onClick={() => handleDeleteQuickLink(link.id)}>Delete</button>
                                                        </div>
                                                </>
                                        )}
                                </div>
                        ))}

                        {quickLinks.length < 5 && (
                                <div className="Account__link-divs Account__quick-link-item Account__quick-link-add">
                                        <div className="Account__quick-link-header">
                                                <i className="fa-solid fa-link Account__icons"></i>
                                                <span className="Account__quick-link-label">New Link:</span>
                                        </div>
                                        <input
                                                type="text"
                                                value={newQuickLink.name}
                                                placeholder="Link Name"
                                                onChange={e => setNewQuickLink({ ...newQuickLink, name: e.target.value })}
                                                className="Account__input-custom-title"
                                        />
                                        <input
                                                type="text"
                                                value={newQuickLink.url}
                                                placeholder="Link URL"
                                                onChange={e => setNewQuickLink({ ...newQuickLink, url: e.target.value })}
                                                className="Account__edit-input"
                                        />
                                        <div className="Account__quick-link-buttons">
                                                <button className="Account__navigation-buttons" onClick={handleAddQuickLink}>Add</button>
                                        </div>
                                </div>
                        )}

                        <div style={{ marginTop: "32px" }}>
                                <p className="small">
                                        Click the link below to view our privacy policy.
                                </p>
                                <a href="/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" style={{ color: "#3f66a0", textDecoration: "underline" }}>
                                        Privacy Policy
                                </a>
                        </div>
                </>
        );

        const renderApiKeys = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">API Keys</h2>
                                <p className="Account__section-desc">Generate API keys to allow external tools and agents to read, create, and manage your documents programmatically. Each key is shown only once — save it somewhere safe.</p>
                        </div>

                        <div style={{ background: '#f0f4ff', border: '1px solid #c5d0f5', borderRadius: '8px', padding: '16px', marginBottom: '24px', fontSize: '13px', lineHeight: '1.7', color: '#1a1a1a' }}>
                                <p style={{ fontWeight: 600, marginBottom: '8px' }}>
                                        <i className="fas fa-circle-info" style={{ marginRight: '8px', color: '#3b5bdb' }}></i>
                                        How to use your API key
                                </p>
                                <p style={{ marginBottom: '8px' }}>After generating a key, store it in a <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>.env</code> file in your project:</p>
                                <code style={{ display: 'block', background: '#e8ecfd', color: '#1a1a1a', padding: '8px 12px', borderRadius: '4px', fontFamily: 'monospace', marginBottom: '10px' }}>
                                        BLUEBIRD_API_KEY=bb_your_key_here
                                </code>
                                <p style={{ marginBottom: '8px' }}>Then pass the key as the <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>Authorization</code> header in requests:</p>
                                <code style={{ display: 'block', background: '#e8ecfd', color: '#1a1a1a', padding: '8px 12px', borderRadius: '4px', fontFamily: 'monospace', marginBottom: '10px' }}>
                                        Authorization: bb_your_key_here
                                </code>
                                <p style={{ marginBottom: '4px' }}>Key endpoints (base URL: <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>bluebirddocumentationadmin.pythonanywhere.com</code>):</p>
                                <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                                        <li><code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>GET /agent/documents/</code> — list your documents</li>
                                        <li><code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>POST /agent/documents/</code> — create a document</li>
                                        <li><code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>GET /agent/documents/&#123;type&#125;/&#123;name&#125;/</code> — read a document</li>
                                        <li><code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>PUT /agent/documents/&#123;type&#125;/&#123;name&#125;/</code> — update a document</li>
                                        <li><code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>DELETE /agent/documents/&#123;type&#125;/&#123;name&#125;/</code> — delete a document</li>
                                </ul>
                                <p style={{ margin: 0 }}>Document content is sent and received as <strong>plain HTML</strong> (e.g. <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>&lt;h1&gt;</code>, <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>&lt;p&gt;</code>, <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>&lt;ul&gt;</code>). View the full spec at <code style={{ background: '#e8ecfd', color: '#1a1a1a', padding: '1px 5px', borderRadius: '3px' }}>/agent/spec/</code>.</p>
                        </div>

                        {generatedKey && (
                                <div style={{ background: '#fff8e1', border: '1px solid #f9a825', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                                        <p style={{ fontWeight: 600, marginBottom: '10px', color: '#5d4037' }}>
                                                <i className="fas fa-triangle-exclamation" style={{ marginRight: '8px' }}></i>
                                                Save this key now — it will not be shown again.
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <code style={{ flex: 1, background: '#f5f5f5', color: '#1a1a1a', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 12px', fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                                                        {generatedKey}
                                                </code>
                                                <button
                                                        className="Account__navigation-buttons"
                                                        onClick={() => { navigator.clipboard.writeText(generatedKey); alert('Key copied to clipboard!'); }}
                                                        style={{ marginBottom: 0 }}
                                                >
                                                        <i className="fas fa-copy" style={{ marginRight: '6px' }}></i>Copy
                                                </button>
                                        </div>
                                        <button
                                                className="Account__navigation-buttons"
                                                style={{ marginTop: '12px' }}
                                                onClick={() => setGeneratedKey(null)}
                                        >
                                                I've saved my key
                                        </button>
                                </div>
                        )}

                        {apiKeysError && <div style={{ color: 'red', marginBottom: '12px' }}>{apiKeysError}</div>}

                        {apiKeys.length > 0 && (
                                <div style={{ marginBottom: '28px' }}>
                                        {apiKeys.map(key => (
                                                <div key={key.id} className="Account__link-divs Account__quick-link-item">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                                                <div>
                                                                        <span style={{ fontWeight: 600 }}>{key.name}</span>
                                                                        <span style={{ marginLeft: '8px', fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>{key.key_prefix}…</span>
                                                                        {!key.is_active && <span style={{ marginLeft: '8px', color: '#c0392b', fontSize: '12px' }}>(Revoked)</span>}
                                                                </div>
                                                                {key.is_active && (
                                                                        <button
                                                                                className="Account__navigation-buttons Account__quick-link-delete"
                                                                                onClick={() => revokeApiKey(key.id, key.name)}
                                                                        >
                                                                                Revoke
                                                                        </button>
                                                                )}
                                                        </div>
                                                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#666', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                                                <span>Scopes: <strong>{key.scopes.join(', ')}</strong></span>
                                                                <span>{key.requests_per_hour} req/hr</span>
                                                                {key.last_used_at && <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>}
                                                                {key.expires_at && <span>Expires: {new Date(key.expires_at).toLocaleDateString()}</span>}
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                        )}

                        <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Create New API Key</h3>

                                <div className="Account__field-row">
                                        <label className="Account__field-label">Key Name</label>
                                        <input
                                                type="text"
                                                placeholder="e.g. Claude Code, Automation Script"
                                                value={newKeyName}
                                                onChange={(e) => setNewKeyName(e.target.value)}
                                                className="Account__edit-input"
                                        />
                                </div>

                                <div className="Account__field-row">
                                        <label className="Account__field-label">Permissions</label>
                                        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                                {['read', 'write', 'delete'].map(scope => (
                                                        <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
                                                                <input
                                                                        type="checkbox"
                                                                        checked={newKeyScopes[scope]}
                                                                        onChange={(e) => setNewKeyScopes(prev => ({ ...prev, [scope]: e.target.checked }))}
                                                                />
                                                                <span style={{ textTransform: 'capitalize' }}>{scope}</span>
                                                        </label>
                                                ))}
                                        </div>
                                </div>

                                <div className="Account__field-row">
                                        <label className="Account__field-label">Rate Limit</label>
                                        <select
                                                className="Account__select-field"
                                                value={newKeyRPH}
                                                onChange={(e) => setNewKeyRPH(parseInt(e.target.value))}
                                        >
                                                <option value={50}>50 requests / hour</option>
                                                <option value={100}>100 requests / hour</option>
                                                <option value={200}>200 requests / hour</option>
                                                <option value={500}>500 requests / hour</option>
                                        </select>
                                </div>

                                <button
                                        className="Account__navigation-buttons"
                                        onClick={createApiKey}
                                        disabled={apiKeysLoading}
                                        style={{ marginTop: '4px' }}
                                >
                                        {apiKeysLoading ? 'Generating…' : 'Generate Key'}
                                </button>
                        </div>
                </>
        );

        const renderBlockedEmails = () => (
                <>
                        <div className="Account__section-header">
                                <h2 className="Account__section-title">Blocked Emails</h2>
                                <p className="Account__section-desc">Block someone by email address. Blocked people cannot view or comment on your documents, cannot be invited to your workspaces, boards or events, and cannot share anything with you.</p>
                        </div>

                        <div style={{ background: '#f0f4ff', border: '1px solid #c5d0f5', borderRadius: '8px', padding: '16px', marginBottom: '24px', fontSize: '13px', lineHeight: '1.7', color: '#1a1a1a' }}>
                                <p style={{ fontWeight: 600, marginBottom: '8px' }}>
                                        <i className="fas fa-circle-info" style={{ marginRight: '8px', color: '#3b5bdb' }}></i>
                                        What blocking does — and what it cannot do
                                </p>
                                <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                                        <li>Your documents, workspaces, boards and calendar events stop being visible to them, and they can no longer comment, mention you, or send you an invitation.</li>
                                        <li><strong>Documents you have made public stay public.</strong> Anyone can read a public document without an account, so a blocked person who simply signs out can still open it. If you need a document hidden from them, make it private.</li>
                                        <li>Their existing comments on your documents are hidden rather than deleted.</li>
                                        <li>Anything already sent — a share email, an invitation — cannot be recalled.</li>
                                        <li>Unblocking restores exactly what they had before: shared documents, memberships and hidden comments all come back.</li>
                                </ul>
                                <p style={{ margin: 0 }}>The person you block is never told. To them, your documents simply look unavailable.</p>
                        </div>

                        {blockedError && <div style={{ color: 'red', marginBottom: '12px' }}>{blockedError}</div>}

                        {blockedEmails.length === 0 ? (
                                <p style={{ color: '#666', marginBottom: '28px' }}>You have not blocked anyone.</p>
                        ) : (
                                <div style={{ marginBottom: '28px' }}>
                                        {blockedEmails.map(entry => (
                                                <div key={entry.id} className="Account__link-divs Account__quick-link-item">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                                                <div>
                                                                        <span style={{ fontWeight: 600 }}>{entry.email}</span>
                                                                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
                                                                                Blocked {new Date(entry.created_at).toLocaleDateString()}
                                                                        </div>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                        {editingBlockId === entry.id ? (
                                                                                <>
                                                                                        <button className="Account__navigation-buttons" onClick={() => saveBlockedNote(entry.id)}>Save</button>
                                                                                        <button className="Account__navigation-buttons Account__quick-link-delete" onClick={() => { setEditingBlockId(null); setEditingBlockNote(''); }}>Cancel</button>
                                                                                </>
                                                                        ) : (
                                                                                <>
                                                                                        <button
                                                                                                className="Account__navigation-buttons"
                                                                                                onClick={() => { setEditingBlockId(entry.id); setEditingBlockNote(entry.note || ''); }}
                                                                                        >
                                                                                                {entry.note ? 'Edit Note' : 'Add Note'}
                                                                                        </button>
                                                                                        <button
                                                                                                className="Account__navigation-buttons Account__quick-link-delete"
                                                                                                onClick={() => removeBlockedEmail(entry.id, entry.email)}
                                                                                        >
                                                                                                Unblock
                                                                                        </button>
                                                                                </>
                                                                        )}
                                                                </div>
                                                        </div>
                                                        {editingBlockId === entry.id ? (
                                                                <input
                                                                        type="text"
                                                                        className="Account__edit-input"
                                                                        style={{ marginTop: '10px' }}
                                                                        placeholder="Why you blocked this person (only you can see this)"
                                                                        value={editingBlockNote}
                                                                        onChange={(e) => setEditingBlockNote(e.target.value)}
                                                                />
                                                        ) : entry.note ? (
                                                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>{entry.note}</div>
                                                        ) : null}
                                                </div>
                                        ))}
                                </div>
                        )}

                        <div style={{ borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Block an Email Address</h3>

                                <div className="Account__field-row">
                                        <label className="Account__field-label">Email</label>
                                        <input
                                                type="email"
                                                placeholder="person@example.com"
                                                value={newBlockedEmail}
                                                onChange={(e) => setNewBlockedEmail(e.target.value)}
                                                className="Account__edit-input"
                                        />
                                </div>

                                <div className="Account__field-row">
                                        <label className="Account__field-label">Note (optional)</label>
                                        <input
                                                type="text"
                                                placeholder="Only you can see this"
                                                value={newBlockedNote}
                                                onChange={(e) => setNewBlockedNote(e.target.value)}
                                                className="Account__edit-input"
                                        />
                                </div>

                                <button
                                        className="Account__navigation-buttons"
                                        onClick={addBlockedEmail}
                                        disabled={blockedLoading}
                                        style={{ marginTop: '4px' }}
                                >
                                        {blockedLoading ? 'Blocking\u2026' : 'Block Address'}
                                </button>
                        </div>
                </>
        );

        const renderSection = () => {
                switch (activeSection) {
                        case "profile":       return renderProfile();
                        case "social":        return renderSocial();
                        case "notifications": return renderNotifications();
                        case "preferences":   return renderPreferences();
                        case "quicklinks":    return renderQuickLinks();
                        case "apikeys":       return renderApiKeys();
                        case "blocked":       return renderBlockedEmails();
                        default:              return renderProfile();
                }
        };

        return (
                <div
                        className="Account__shell"
                        onContextMenu={enhancedRightClickEnabled ? handleContextMenu : undefined}
                >
                        {/* Sidebar */}
                        <aside className="Account__sidebar">
                                <div className="Account__sidebar-header">
                                        <p className="Account__sidebar-title">Settings</p>
                                        <p className="Account__sidebar-user">{accountName || accountEmail}</p>
                                </div>

                                <nav className="Account__nav">
                                        <div className="Account__nav-group">
                                                <p className="Account__nav-group-label">Account</p>
                                                <button
                                                        className={`Account__nav-item${activeSection === "profile" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("profile")}
                                                >
                                                        <i className="fas fa-user"></i>
                                                        <span>Profile</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "social" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("social")}
                                                >
                                                        <i className="fas fa-share-alt"></i>
                                                        <span>Social Links</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "notifications" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("notifications")}
                                                >
                                                        <i className="fas fa-bell"></i>
                                                        <span>Notifications</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "preferences" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("preferences")}
                                                >
                                                        <i className="fas fa-sliders"></i>
                                                        <span>Preferences</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "quicklinks" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("quicklinks")}
                                                >
                                                        <i className="fas fa-link"></i>
                                                        <span>Quick Links</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "apikeys" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("apikeys")}
                                                >
                                                        <i className="fas fa-key"></i>
                                                        <span>API Keys</span>
                                                </button>
                                                <button
                                                        className={`Account__nav-item${activeSection === "blocked" ? " Account__nav-item--active" : ""}`}
                                                        onClick={() => setActiveSection("blocked")}
                                                >
                                                        <i className="fas fa-user-slash"></i>
                                                        <span>Blocked Emails</span>
                                                </button>
                                        </div>
                                </nav>

                                <div className="Account__sidebar-footer">
                                        <button
                                                className="Account__back-link"
                                                onClick={handleBackToHome}
                                                title="Back to home"
                                        >
                                                <i className="fas fa-arrow-left"></i>
                                                <span>Home</span>
                                        </button>
                                        <button
                                                className="Account__back-link"
                                                onClick={handleNotebookSettingsClick}
                                                title="Notebook Settings"
                                        >
                                                <i className="fa-solid fa-sliders"></i>
                                                <span>Notebook Settings</span>
                                        </button>
                                        <button
                                                className="Account__back-link"
                                                onClick={() => navigate('/templates')}
                                                title="Manage your document templates"
                                        >
                                                <i className="fas fa-puzzle-piece"></i>
                                                <span>My Templates</span>
                                        </button>
                                        <button
                                                className="Account__back-link"
                                                onClick={() => navigate(`/account/${uid}/storage`)}
                                                title="Manage storage and billing"
                                        >
                                                <i className="fas fa-hard-drive"></i>
                                                <span>Storage &amp; Billing</span>
                                        </button>
                                        <button
                                                className="Account__back-link"
                                                onClick={() => navigate('/delete-account')}
                                                title="Permanently delete your account"
                                                style={{ color: "#e74c3c" }}
                                        >
                                                <i className="fas fa-trash"></i>
                                                <span>Delete Account</span>
                                        </button>
                                </div>
                        </aside>

                        {/* Main content */}
                        <main className="Account__main">
                                {renderSection()}
                        </main>

                        {enhancedRightClickEnabled && ContextMenuComponent}
                </div>
        );

}

export default Account